import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup as MuiRadioGroup,
  RadioGroupProps as MuiRadioGroupProps,
  Typography,
} from '@mui/material'
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form'

export type RadioOption = {
  label: string
  value: string | boolean
}

export type RadioGroupProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  // name will come from controller
> = Omit<MuiRadioGroupProps, 'name'> & {
  name: TName
  control: Control<TFieldValues>
  label?: string
  options: RadioOption[]
  helperText?: string
  fullWidth?: boolean
  disclaimerText?: string
  isBooleanChoice?: boolean
}

export const RadioGroup = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  label,
  options,
  helperText,
  disclaimerText,
  isBooleanChoice,
  ...radioGroupProps
}: RadioGroupProps<TFieldValues, TName>) => {
  const { onChange, ...restRadioGroupProps } = radioGroupProps

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl error={!!error} fullWidth>
          {label && <Typography variant="h6">{label}</Typography>}
          {disclaimerText && (
            <Typography sx={{ color: 'grey.500' }} variant="body2">
              {disclaimerText}
            </Typography>
          )}
          <MuiRadioGroup
            {...field}
            {...restRadioGroupProps}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (isBooleanChoice) {
                const value = e.target.value === 'true' ? true : false // soft parse for boolean
                field.onChange(value)
              } else {
                field.onChange(e.target.value)
              }
              onChange?.(e, e.target.value)
            }}
          >
            {options.map((option) => (
              <FormControlLabel
                key={option.label}
                value={option.value}
                control={<Radio />}
                label={option.label}
                defaultChecked={false}
                sx={{ '.MuiTypography-root': { lineHeight: 1.2 } }}
              />
            ))}
          </MuiRadioGroup>
          {(error?.message || helperText) && (
            <FormHelperText>{error?.message || helperText}</FormHelperText>
          )}
        </FormControl>
      )}
    />
  )
}
